// node_modules
import { FC, useCallback, useContext, useState } from "react";
import { useHistory, useRouteMatch } from "react-router-dom";
import { faChevronDown, faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// Components
import { SortByContainer, TitledChild } from "Components";
import { ConceptListItem } from "../ConceptListItem/ConceptListItem";
import { TechnologyListItem } from "../TechnologyListItem/TechnologyListItem";
// Enums
import { CaseActionTypeEnum, CasePageEnum, TechnologyVoteTypeEnum, VoteTypeEnum } from "Enums";
// Interfaces
import { ICasePhaseAction } from "Interfaces";
// Contexts
import { CaseContext, AuthContext } from "Stores";
// styles
import styles from "../conceptTechnologyList.module.scss";
// Types
import { TConcept, TTechnology, TTechnologyRatesDTO, TTechnologyVotesDTO } from "Types";
// Constants
import { ConceptConstants } from "Constants";

type TPhaseListItemProps = {
    phaseName: string,
    phaseNameConstant: string,
    votingType: VoteTypeEnum,
    isAdditionalResultsEnabled?: boolean,
    setDisplaySelected: (casePageName: CasePageEnum) => void;
}

export const PhaseListItem: FC<TPhaseListItemProps> = ({ phaseName, phaseNameConstant, votingType, isAdditionalResultsEnabled, setDisplaySelected }: TPhaseListItemProps) => {
    // State
    const [currentSorting, setCurrentSorting] = useState("Number");

    // Shared states
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);

    // Logic
    // `url` lets
    // us build relative links.
    const { url } = useRouteMatch();

    // We get history to navigate
    const history = useHistory();

    // Logic
    const setSelectedPhaseName = useCallback((text: string): void => {
        history.replace(`${url}/${text.toLowerCase()}`);
        caseDispatch({ type: CaseActionTypeEnum.SetCasePhase, casePhase: text } as ICasePhaseAction);
    }, [caseDispatch, history, url]);

    const overviewClassname = (): string => {
        const classNameList = [styles.listItem];
        if (caseState.selectedEntityId && caseState.selectedEntityName && caseState.selectedEntityName.toLocaleLowerCase() === CasePageEnum.Overview) classNameList.push(styles.active);
        return classNameList.join(" ");
    };

    const radarClassname = (): string => {
        const classNameList = [styles.listItem];
        if (caseState.selectedEntityId && caseState.selectedEntityName && caseState.selectedEntityName.toLocaleLowerCase() === CasePageEnum.Radar) classNameList.push(styles.active);
        return classNameList.join(" ");
    };
    
    const requirementsTableClassname = (): string => {
        const classNameList = [styles.listItem];
        if (caseState.selectedEntityId && caseState.selectedEntityName && caseState.selectedEntityName.toLocaleLowerCase() === CasePageEnum.RequiremenstTable) classNameList.push(styles.active);
        return classNameList.join(" ");
    };

    const getTechnologyListSortedOnVotes = (fromConcepts: TConcept[] | undefined): TTechnology[] => {
        if (!fromConcepts) return [];
        const allTechnology: TTechnology[] = fromConcepts.map(concept => concept.technologies).flat();
        return allTechnology.sort((technologyA, technologyB) => compareTechnologyOnVotes(technologyA.votes, technologyB.votes));
    };

    const getTechnologyListSortedOnRates = (fromConcepts: TConcept[] | undefined): TTechnology[] => {
        if (!fromConcepts) return [];
        const allTechnology: TTechnology[] = fromConcepts.map(concept => concept.technologies).flat();
        return allTechnology.sort((technologyA, technologyB) => compareTechnologyOnRates(technologyA.rates, technologyB.rates));
    };

    const compareTechnologyOnVotes = (votesA: TTechnologyVotesDTO | undefined, votesB: TTechnologyVotesDTO | undefined): number => {
        if (!votesA && !votesB) return 0;
        if (!votesA && votesB) return 1;
        if (votesA && !votesB) return -1;
        if (votesA && votesB) {
            const compareValue = votesB.averageVote - votesA.averageVote;
            if (compareValue !== 0) return compareValue;
            // if the compare value is equals to 0, need to compare them 
            // based on the number of super likes each technology has
            let votesASuperLikeCount = 0;
            let votesBSuperLikeCount = 0;

            if (votesA.currentUserVote && votesA.currentUserVote.vote === TechnologyVoteTypeEnum.Super) votesASuperLikeCount++;
            if (votesB.currentUserVote && votesB.currentUserVote.vote === TechnologyVoteTypeEnum.Super) votesBSuperLikeCount++;

            if (votesA.userVotes && votesA.userVotes.length > 0) {
                votesASuperLikeCount += votesA.userVotes.filter(vote => vote.vote === TechnologyVoteTypeEnum.Super).length;
            }
            if (votesB.userVotes && votesB.userVotes.length > 0) {
                votesBSuperLikeCount += votesB.userVotes.filter(vote => vote.vote === TechnologyVoteTypeEnum.Super).length;
            }

            return votesBSuperLikeCount - votesASuperLikeCount;
        }
        return 0;
    };

    const compareTechnologyOnRates = (ratesA: TTechnologyRatesDTO | undefined, ratesB: TTechnologyRatesDTO | undefined): number => {
        if (!ratesA && !ratesB) return 0;
        if (!ratesA && ratesB) return 1;
        if (ratesA && !ratesB) return -1;
        if (ratesA && ratesB) return ratesB.averageRate - ratesA.averageRate;
        return 0;
    };

    return (
        <div className={styles.phaseContainer}>
            <div className={[styles.phaseHeader, caseState.casePhase.toLocaleLowerCase() === phaseNameConstant.toLocaleLowerCase() ? styles.activePhase : null].join(" ")} onClick={() => setSelectedPhaseName(phaseNameConstant.toLowerCase())}>
                <div className={styles.phaseTitle}>
                    <div className={styles.iconContainer}>
                        {caseState.casePhase.toLowerCase() === phaseNameConstant.toLowerCase() ? <FontAwesomeIcon icon={faChevronDown} /> : null}
                        {caseState.casePhase.toLowerCase() !== phaseNameConstant.toLowerCase() ? <FontAwesomeIcon icon={faChevronRight} /> : null}
                    </div>
                    <span>{phaseName}</span>
                </div>
                {caseState.casePhase.toLowerCase() === phaseNameConstant.toLowerCase() ?
                    <SortByContainer extraStyle={{ flex: 1, textAlign: "right" }} selectedName={currentSorting} onSortingChange={setCurrentSorting}>
                        <TitledChild title="Number"><></></TitledChild>
                        { votingType === VoteTypeEnum.YesNoSuper ? <TitledChild title="Vote"><></></TitledChild> : null }
                        { votingType === VoteTypeEnum.FiveStars ? <TitledChild title="Rate"><></></TitledChild> : null }
                    </SortByContainer>
                    :
                    null
                }
            </div>
            {caseState.casePhase.toLowerCase() === phaseNameConstant.toLowerCase() ?
                 <div className={[styles.phaseContentContainer, styles.scrollbarCustomThin].join(" ")}>
                    <div className={overviewClassname()} onClick={() => { setDisplaySelected(CasePageEnum.Overview); }}>
                        <span>Overview</span>
                    </div>
                    {caseState.conceptTechnologyList?.publishedPhaseOptions.doShowRadar ?
                        <div className={radarClassname()} onClick={() => { setDisplaySelected(CasePageEnum.Radar); }}>
                            <span>Maturity radar</span>
                        </div>
                        :
                        null
                    }
                    {caseState.conceptTechnologyList?.publishedPhaseOptions.doShowRequirementsTable ?
                        <div className={requirementsTableClassname()} onClick={() => { setDisplaySelected(CasePageEnum.RequiremenstTable); }}>
                            <span>Requirements table</span>
                        </div>
                        :
                        null
                    }
                    {currentSorting === "Number" ?
                        caseState.conceptTechnologyList?.concepts?.map((currentConcept) => {
                            return (<ConceptListItem key={currentConcept.id} concept={currentConcept} />);
                        })
                        :
                        null
                    }
                    {currentSorting === "Vote" ?
                        getTechnologyListSortedOnVotes(caseState.conceptTechnologyList?.concepts).map((technology) => {
                            return <TechnologyListItem key={technology.id} technology={technology} />;
                        })
                        :
                        null
                    }
                    {currentSorting === "Rate" ?
                        getTechnologyListSortedOnRates(caseState.conceptTechnologyList?.concepts).map((technology) => {
                            return <TechnologyListItem key={technology.id} technology={technology} />;
                        })
                        : null
                    }
                    {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery && isAdditionalResultsEnabled ?
                        <ConceptListItem concept={{
                            id: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_ID, name: ConceptConstants.FIXED_ADDITIONAL_CONCEPT_NAME,
                            numbering: `${caseState.conceptTechnologyList ? caseState.conceptTechnologyList.concepts.length + 1 : 1}.`,
                            technologies: caseState.conceptTechnologyList ? caseState.conceptTechnologyList.additionalTechnologies : []
                        }}
                            doEnableAddingTechnologies={true} />
                        :
                        null
                    }
                </div>
                :
                null
            }
        </div>
    );
};